import { useState, useCallback } from 'react';
import { apiClient } from '../utils/apiClient';
import {
  ConversationSearchRequest,
  ConversationListResponse,
  ConversationUpdateRequest,
  ConversationExportRequest,
  ConversationShareRequest,
  ConversationDeleteRequest
} from '@nvgc/shared';

export interface ConversationManagementHook {
  searchConversations: (request: ConversationSearchRequest) => Promise<ConversationListResponse>;
  updateConversation: (conversationId: string, updates: ConversationUpdateRequest) => Promise<void>;
  exportConversation: (request: ConversationExportRequest) => Promise<void>;
  shareConversation: (request: ConversationShareRequest) => Promise<void>;
  deleteConversation: (request: ConversationDeleteRequest) => Promise<void>;
  getAvailableTags: () => Promise<string[]>;
  isLoading: boolean;
  error: string | null;
}

export function useConversationManagement(): ConversationManagementHook {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const searchConversations = useCallback(async (request: ConversationSearchRequest): Promise<ConversationListResponse> => {
    setIsLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams();
      if (request.query) params.append('query', request.query);
      if (request.tags && request.tags.length > 0) params.append('tags', request.tags.join(','));
      if (request.favoritesOnly) params.append('favoritesOnly', 'true');
      params.append('page', request.page.toString());
      params.append('pageSize', request.pageSize.toString());

      const response = await apiClient.get<ConversationListResponse>(`/api/conversations/search?${params.toString()}`);
      
      if (!response.ok) {
        throw new Error('Failed to search conversations');
      }
      
      const data = response.data;
      
      // Transform dates from strings to Date objects
      const conversations = data.conversations.map((conv: any) => ({
        ...conv,
        createdAt: new Date(conv.createdAt as string),
        updatedAt: new Date(conv.updatedAt as string),
        lastMessageAt: conv.lastMessageAt ? new Date(conv.lastMessageAt as string) : undefined
      }));
      
      return {
        ...data,
        conversations
      };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to search conversations';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const updateConversation = useCallback(async (conversationId: string, updates: ConversationUpdateRequest): Promise<void> => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await apiClient.patch(`/api/conversations/${conversationId}`, updates);
      
      if (!response.ok) {
        throw new Error('Failed to update conversation');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update conversation';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const exportConversation = useCallback(async (request: ConversationExportRequest): Promise<void> => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await apiClient.post<any>('/api/conversations/export', request);
      
      if (!response.ok) {
        throw new Error('Failed to export conversation');
      }
      
      const result = response.data;
      
      // If export is completed immediately, trigger download
      if (result.status === 'completed' && result.downloadUrl) {
        window.open(result.downloadUrl, '_blank');
      } else {
        // Show notification that export is processing
        console.log('Export is processing, you will be notified when ready');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to export conversation';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const shareConversation = useCallback(async (request: ConversationShareRequest): Promise<void> => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await apiClient.post('/api/conversations/share', request);
      
      if (!response.ok) {
        throw new Error('Failed to share conversation');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to share conversation';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const deleteConversation = useCallback(async (request: ConversationDeleteRequest): Promise<void> => {
    setIsLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams();
      if (request.permanent) params.append('permanent', 'true');
      
      const response = await apiClient.delete(`/api/conversations/${request.conversationId}?${params.toString()}`);
      
      if (!response.ok) {
        throw new Error('Failed to delete conversation');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete conversation';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const getAvailableTags = useCallback(async (): Promise<string[]> => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await apiClient.get<{ tags: string[] }>('/api/conversations/tags');
      
      if (!response.ok) {
        throw new Error('Failed to get available tags');
      }
      
      const data = response.data;
      return data.tags || [];
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to get available tags';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    searchConversations,
    updateConversation,
    exportConversation,
    shareConversation,
    deleteConversation,
    getAvailableTags,
    isLoading,
    error
  };
}